// src/admin/PostExOrderForm.js
import React, { useState, useEffect } from 'react';
import { X, Copy, Check, ExternalLink, Send, AlertCircle } from 'lucide-react';
import SuccessModal from '../components/SuccessModal';

const PostExOrderForm = ({ order, onClose }) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [copied, setCopied] = useState('');
  const [saving, setSaving] = useState(false);

  // ✅ Default PostEx Form Data
  const defaultForm = {
    orderType: 'Normal',
    orderRefNumber: '',
    orderAmount: 0,
    orderDate: new Date().toISOString().split('T')[0],
    customerName: '',
    customerPhone: '',
    deliveryCity: '',
    deliveryAddress: '',
    airwayBillCopies: 1,
    items: 1,
    pickupCity: 'Rawalpindi',
    pickupAddress: 'Adyala Road Rawalpindi',
    returnCity: 'Rawalpindi',
    returnAddress: 'Adyala Road Rawalpindi',
    bookingWeight: 0.5,
    orderDetail: '',
    notes: '',
  };

  const [form, setForm] = useState(defaultForm);

  // ✅ Auto-fill from order
  useEffect(() => {
    if (!order) return;

    // Order Reference Number = "Mr" + order ID (last 6 chars)
    const orderId = order.id?.slice(-6).toUpperCase() || '000000';
    const orderRefNumber = `Mr${orderId}`;

    // Order Detail = product names with quantities
    const orderDetail = (order.items || [])
      .map(item => `${item.title} x${item.quantity}`)
      .join(', ');

    setForm({
      ...defaultForm,
      orderRefNumber,
      orderAmount: order.total || 0,
      orderDate: new Date().toISOString().split('T')[0],
      customerName: order.customerName || '',
      customerPhone: order.phone || '',
      deliveryCity: order.city || '',
      deliveryAddress: `${order.address || ''}${order.state ? ', ' + order.state : ''}${order.postalCode ? ' - ' + order.postalCode : ''}`,
      orderDetail,
      notes: '', // Always empty
    });
  }, [order]);

  // Copy to clipboard
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 1500);
  };

  // Copy all fields for pasting into PostEx
  const copyAllForPostEx = () => {
    const text = `
Order Type: ${form.orderType}
Order Reference Number: ${form.orderRefNumber}
Order Amount: ${form.orderAmount}
Order Date: ${form.orderDate}
Customer Name: ${form.customerName}
Customer Contact: ${form.customerPhone}
Delivery City: ${form.deliveryCity}
Delivery Address: ${form.deliveryAddress}
Airway Bill Copies: ${form.airwayBillCopies}
Items: ${form.items}
Pickup City: ${form.pickupCity}
Pickup Address: ${form.pickupAddress}
Return City: ${form.returnCity}
Return Address: ${form.returnAddress}
Booking Weight (kg): ${form.bookingWeight}
Order Detail: ${form.orderDetail}
Notes: ${form.notes}
    `.trim();

    navigator.clipboard.writeText(text);
    setCopied('all');
    setTimeout(() => setCopied(''), 2000);
  };

  // ✅ Save to Firestore + Open PostEx in new tab
  const handleBookOnPostEx = async () => {
    setSaving(true);
    try {
      // 1. Save form data to Firestore order document (for tracking)
      const { doc, updateDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase');

      await updateDoc(doc(db, 'orders', order.id), {
        postexForm: form,
        postexBookedAt: new Date(),
      });

      // 2. Copy all details to clipboard (for easy paste)
      copyAllForPostEx();

      // 3. Open PostEx Create Order page
      window.open('https://merchant.postex.pk/main/create-order', '_blank');

      setShowSuccess(true);
      setTimeout(() => {
        setSaving(false);
      }, 1000);
    } catch (error) {
      console.error('Error:', error);
      setSaving(false);
      alert('Failed to save. Try again.');
    }
  };

  if (!order) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 anim-fadeIn"
      style={{ backgroundColor: 'rgba(7, 27, 54, 0.7)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto anim-popIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-800">
              📦 PostEx Booking Form
            </h2>
            <p className="text-xs text-gray-500">
              Order ID: #{order.id?.slice(-6).toUpperCase()}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:bg-gray-100 p-2 rounded-lg">
            <X size={20} />
          </button>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border-b border-blue-200 p-4">
          <div className="flex items-start gap-2">
            <AlertCircle size={16} className="text-blue-600 mt-0.5 shrink-0" />
            <div className="text-xs text-blue-800">
              <p className="font-bold mb-1">📋 Instructions:</p>
              <ol className="list-decimal ml-4 space-y-0.5">
                <li>Neeche di gayi details **"Copy All"** button se copy karein</li>
                <li>**"Book on PostEx"** button dabayein — PostEx ka Create Order page khulega</li>
                <li>Wahan fields mein paste karein</li>
                <li>PostEx se **Consignment Number (CN)** milega</li>
                <li>Wo CN wapas apni website ke order mein daalein</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="p-5 space-y-4">

          {/* Order Type */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Order Type *</label>
            <input
              type="text"
              value={form.orderType}
              readOnly
              className="w-full border border-gray-200 rounded-lg py-2.5 px-3 text-sm bg-gray-50 cursor-not-allowed"
            />
          </div>

          {/* Order Reference Number */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Order Reference Number * <span className="text-pink-500">(Auto: "Mr" + ID)</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={form.orderRefNumber}
                readOnly
                className="w-full border border-gray-200 rounded-lg py-2.5 px-3 pr-10 text-sm bg-gray-50 font-mono"
              />
              <button
                type="button"
                onClick={() => copyToClipboard(form.orderRefNumber, 'ref')}
                className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600"
              >
                {copied === 'ref' ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
              </button>
            </div>
          </div>

          {/* Order Amount + Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Order Amount * (Rs.)</label>
              <input
                type="text"
                value={`Rs. ${form.orderAmount}`}
                readOnly
                className="w-full border border-gray-200 rounded-lg py-2.5 px-3 text-sm bg-gray-50 font-bold"
                style={{ color: 'var(--color-link)' }}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Order Date *</label>
              <input
                type="date"
                value={form.orderDate}
                onChange={(e) => setForm({ ...form, orderDate: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2.5 px-3 text-sm focus:outline-none focus:border-pink-500"
              />
            </div>
          </div>

          {/* Customer Name + Phone */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Customer Name *</label>
              <input
                type="text"
                value={form.customerName}
                readOnly
                className="w-full border border-gray-200 rounded-lg py-2.5 px-3 text-sm bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Customer Contact *</label>
              <input
                type="text"
                value={form.customerPhone}
                readOnly
                className="w-full border border-gray-200 rounded-lg py-2.5 px-3 text-sm bg-gray-50"
              />
            </div>
          </div>

          {/* Delivery City + Address */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Delivery City *</label>
            <input
              type="text"
              value={form.deliveryCity}
              readOnly
              className="w-full border border-gray-200 rounded-lg py-2.5 px-3 text-sm bg-gray-50"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Delivery Address *</label>
            <textarea
              value={form.deliveryAddress}
              readOnly
              rows={2}
              className="w-full border border-gray-200 rounded-lg py-2.5 px-3 text-sm bg-gray-50 resize-none"
            />
          </div>

          {/* Airway Bill Copies + Items */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Airway Bill Copies *</label>
              <input
                type="number"
                value={form.airwayBillCopies}
                readOnly
                className="w-full border border-gray-200 rounded-lg py-2.5 px-3 text-sm bg-gray-50 text-center font-bold"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Items *</label>
              <input
                type="number"
                value={form.items}
                readOnly
                className="w-full border border-gray-200 rounded-lg py-2.5 px-3 text-sm bg-gray-50 text-center font-bold"
              />
            </div>
          </div>

          {/* Pickup Info */}
          <div className="bg-pink-50 rounded-xl p-3">
            <p className="text-xs font-bold text-pink-700 mb-2">🚚 Pickup Info</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Pickup City *</label>
                <input
                  type="text"
                  value={form.pickupCity}
                  readOnly
                  className="w-full border border-pink-200 rounded-lg py-2.5 px-3 text-sm bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Pickup Address *</label>
                <input
                  type="text"
                  value={form.pickupAddress}
                  readOnly
                  className="w-full border border-pink-200 rounded-lg py-2.5 px-3 text-sm bg-white"
                />
              </div>
            </div>
          </div>

          {/* Return Info */}
          <div className="bg-pink-50 rounded-xl p-3">
            <p className="text-xs font-bold text-pink-700 mb-2">↩️ Return Info</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Return City</label>
                <input
                  type="text"
                  value={form.returnCity}
                  readOnly
                  className="w-full border border-pink-200 rounded-lg py-2.5 px-3 text-sm bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Return Address</label>
                <input
                  type="text"
                  value={form.returnAddress}
                  readOnly
                  className="w-full border border-pink-200 rounded-lg py-2.5 px-3 text-sm bg-white"
                />
              </div>
            </div>
          </div>

          {/* Booking Weight */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Booking Weight (kg) <span className="text-pink-500">(Edit kar sakte hain)</span>
            </label>
            <input
              type="number"
              step="0.1"
              value={form.bookingWeight}
              onChange={(e) => setForm({ ...form, bookingWeight: parseFloat(e.target.value) || 0 })}
              className="w-full border border-gray-300 rounded-lg py-2.5 px-3 text-sm focus:outline-none focus:border-pink-500"
            />
          </div>

          {/* Order Detail */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Order Detail (Product Names)
            </label>
            <textarea
              value={form.orderDetail}
              readOnly
              rows={2}
              className="w-full border border-gray-200 rounded-lg py-2.5 px-3 text-sm bg-gray-50 resize-none"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
            <input
              type="text"
              value={form.notes}
              readOnly
              placeholder="(Always empty)"
              className="w-full border border-gray-200 rounded-lg py-2.5 px-3 text-sm bg-gray-50"
            />
          </div>

        </div>

        {/* Footer Buttons */}
        <div className="p-5 border-t border-gray-200 bg-gray-50 sticky bottom-0 flex gap-2">
          <button
            onClick={copyAllForPostEx}
            className="flex-1 py-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition border-2"
            style={{
              borderColor: 'var(--color-button-bg)',
              color: 'var(--color-link)',
              backgroundColor: 'white',
            }}
          >
            {copied === 'all' ? (
              <><Check size={16} /> Copied!</>
            ) : (
              <><Copy size={16} /> Copy All Fields</>
            )}
          </button>

          <button
            onClick={handleBookOnPostEx}
            disabled={saving}
            className={`flex-1 py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2 btn-2d transition text-white ${
              saving ? 'opacity-70 cursor-not-allowed' : ''
            }`}
            style={{ backgroundColor: 'var(--color-button-bg)' }}
          >
            <ExternalLink size={16} />
            {saving ? 'Opening...' : 'Book on PostEx'}
          </button>
        </div>

      </div>

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Ready to Book!"
        message="PostEx page khul gaya hai. Fields copy ho gaye hain — paste karke book karein."
        buttonText="Got it!"
      />
    </div>
  );
};

export default PostExOrderForm;